/**
 * Running a student's JavaScript against a question's tests.
 *
 * **Where this runs, and why.** In the student's own browser, inside a Web
 * Worker built from a blob, with a hard timeout. Not because that is ideal —
 * a determined student can forge the result — but because there is nowhere
 * else on this stack: Cloudflare Workers refuse dynamic code evaluation
 * outright, so untrusted JavaScript cannot be run server-side without adding a
 * sandbox service.
 *
 * The design is honest about that rather than pretending otherwise. The
 * SOURCE is submitted along with the counts, so anything reported here can be
 * re-run later by a human; the counts alone would be unfalsifiable. And the
 * real backstop is the one this app already leans on everywhere else — the
 * screen recording shows the code being written.
 *
 * **Why a Worker and not an iframe or `new Function`.** A Worker has no DOM,
 * no `window`, no cookies and no access to this page's memory, so a program
 * that goes looking for the answer key finds nothing to look at. More
 * practically, it is the only option that can be KILLED: `while (true) {}` in
 * an iframe or a direct `eval` freezes the exam tab, and a student who writes
 * an accidental infinite loop — which is most students, on a loops paper —
 * would lose their sitting. `worker.terminate()` ends it in milliseconds.
 */
import type { CodeTest, CodeTestResult } from "@exam/shared";

/**
 * A student's loop gets one second. Long enough that nothing correct times
 * out, short enough that running a dozen tests after an infinite loop still
 * feels like a click rather than a hang.
 */
const TIMEOUT_MS = 1_000;

export interface CodeRun {
  results: CodeTestResult[];
  passed: number;
  total: number;
  /** A syntax error or a crash outside any test. */
  error: string | null;
}

/**
 * The worker program, as source.
 *
 * Built here as a string rather than a separate chunk so it cannot fail to
 * load at the moment it is needed — an exam page that fetches its sandbox
 * on demand is an exam page that breaks when the connection does, which on
 * these connections is often.
 */
const WORKER_SOURCE = `
self.onmessage = (event) => {
  const { source, tests } = event.data;
  const results = [];

  // The student's program runs ONCE, and every test then evaluates an
  // expression against whatever it defined. Re-running it per test would let a
  // question accidentally depend on module state and behave differently in the
  // editor than at grading.
  let scope;
  try {
    // Indirect construction, so the student's code sees only its own scope.
    scope = new Function(source + "\\n; return this;").call({});
  } catch (err) {
    self.postMessage({ error: String(err && err.message ? err.message : err), results: [] });
    return;
  }

  for (const test of tests) {
    try {
      const value = new Function(source + "\\n; return (" + test.call + ");")();
      // Compared by JSON shape so arrays and objects work without the author
      // writing a comparator. undefined has no JSON form, so it is spelled out.
      const got = value === undefined ? "undefined" : JSON.stringify(value);
      const want = test.expect === undefined ? "undefined" : JSON.stringify(test.expect);
      results.push(
        got === want
          ? { name: test.name, passed: true, detail: null }
          : {
              name: test.name,
              passed: false,
              detail: "expected " + want + ", got " + got,
            },
      );
    } catch (err) {
      results.push({
        name: test.name,
        passed: false,
        detail: String(err && err.message ? err.message : err).slice(0, 200),
      });
    }
  }

  self.postMessage({ error: null, results });
};
`;

export function runCodeTests(
  source: string,
  tests: readonly CodeTest[],
): Promise<CodeRun> {
  const total = tests.length;

  if (source.trim() === "") {
    return Promise.resolve({ results: [], passed: 0, total, error: null });
  }

  return new Promise<CodeRun>((resolve) => {
    let url: string | null = null;
    let worker: Worker | null = null;
    let settled = false;

    const finish = (run: CodeRun) => {
      if (settled) return;
      settled = true;
      // Terminate BEFORE revoking: a worker still running an infinite loop
      // keeps a core busy for the rest of the exam otherwise, and the student
      // would feel it as the whole page becoming slow.
      worker?.terminate();
      if (url) URL.revokeObjectURL(url);
      resolve(run);
    };

    try {
      url = URL.createObjectURL(
        new Blob([WORKER_SOURCE], { type: "text/javascript" }),
      );
      worker = new Worker(url);
    } catch (err) {
      finish({
        results: [],
        passed: 0,
        total,
        error: err instanceof Error ? err.message : "Could not start the runner",
      });
      return;
    }

    const timer = setTimeout(() => {
      // Everything unfinished is reported as a timeout rather than silently
      // scoring zero: "your code did not finish" is actionable, and a blank
      // failure on a loops question is exactly the wrong thing to leave a
      // student guessing about.
      finish({
        results: tests.map((t) => ({
          name: t.name,
          passed: false,
          detail: "Your code did not finish — check for a loop that never ends.",
        })),
        passed: 0,
        total,
        error: "Timed out after 1 second",
      });
    }, TIMEOUT_MS);

    worker.onmessage = (event: MessageEvent) => {
      clearTimeout(timer);
      const data = event.data as { error: string | null; results: CodeTestResult[] };
      const results = data.results ?? [];
      finish({
        results,
        passed: results.filter((r) => r.passed).length,
        total,
        error: data.error,
      });
    };

    worker.onerror = (event) => {
      clearTimeout(timer);
      finish({
        results: [],
        passed: 0,
        total,
        error: event.message || "The runner crashed",
      });
    };

    worker.postMessage({ source, tests });
  });
}
